// src/components/Modals/ServiceProviderOnboardingModal.tsx

import React, { useState } from 'react';
import { X, Save, Building2, User, Mail, Lock, Phone as PhoneIcon, Shield } from 'lucide-react';
import { organizationsService, CreateServiceProviderRequest } from 'api/services/organizations.service';

interface ServiceProviderOnboardingModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const ServiceProviderOnboardingModal: React.FC<ServiceProviderOnboardingModalProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState<CreateServiceProviderRequest>({
    serviceProviderName: '',
    serviceProviderDescription: '',
    serviceProviderId: '',
    superAdminFirstName: '',
    superAdminLastName: '',
    superAdminEmail: '',
    superAdminPassword: '',
    superAdminPhone: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateServiceProviderRequest, string>>>({});

  const validate = () => {
    const newErrors: Partial<Record<keyof CreateServiceProviderRequest, string>> = {};

    if (!formData.serviceProviderName.trim()) {
      newErrors.serviceProviderName = 'Service provider name is required';
    }
    if (!formData.serviceProviderId.trim()) {
      newErrors.serviceProviderId = 'Service provider ID is required';
    }
    if (!formData.superAdminFirstName.trim()) {
      newErrors.superAdminFirstName = 'First name is required';
    }
    if (!formData.superAdminLastName.trim()) {
      newErrors.superAdminLastName = 'Last name is required';
    }
    if (!formData.superAdminEmail.trim()) {
      newErrors.superAdminEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.superAdminEmail)) {
      newErrors.superAdminEmail = 'Invalid email format';
    }
    if (!formData.superAdminPassword) {
      newErrors.superAdminPassword = 'Password is required';
    } else if (formData.superAdminPassword.length < 6) {
      newErrors.superAdminPassword = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await organizationsService.createServiceProvider(formData);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setErrorMessage(response.message || 'Failed to create service provider');
      }
    } catch (error: any) {
      console.error('Error creating service provider:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to create service provider');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateServiceProviderRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setErrorMessage('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            <h3 className="text-xl font-bold">Onboard New Service Provider</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Error Message */}
          {errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Service Provider Information */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Service Provider Information
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Provider Name *
              </label>
              <input
                type="text"
                value={formData.serviceProviderName}
                onChange={(e) => handleChange('serviceProviderName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.serviceProviderName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="TechVoice Communications"
              />
              {errors.serviceProviderName && (
                <p className="text-xs text-red-500 mt-1">{errors.serviceProviderName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Provider ID *
              </label>
              <input
                type="text"
                value={formData.serviceProviderId}
                onChange={(e) => handleChange('serviceProviderId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.serviceProviderId ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="tech-voice-001"
              />
              {errors.serviceProviderId && (
                <p className="text-xs text-red-500 mt-1">{errors.serviceProviderId}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Use lowercase letters, numbers, and hyphens only
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Provider Description
              </label>
              <textarea
                value={formData.serviceProviderDescription}
                onChange={(e) => handleChange('serviceProviderDescription', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Brief description of the service provider"
                rows={3}
              />
            </div>
          </div>

          {/* Super Admin User Information */}
          <div className="space-y-4 border-t pt-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Super Admin Details
            </h4>
            <p className="text-sm text-gray-600">
              This user will have full administrative access to the service provider organization
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.superAdminFirstName}
                  onChange={(e) => handleChange('superAdminFirstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.superAdminFirstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John"
                />
                {errors.superAdminFirstName && (
                  <p className="text-xs text-red-500 mt-1">{errors.superAdminFirstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.superAdminLastName}
                  onChange={(e) => handleChange('superAdminLastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.superAdminLastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Doe"
                />
                {errors.superAdminLastName && (
                  <p className="text-xs text-red-500 mt-1">{errors.superAdminLastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address *
              </label>
              <input
                type="email"
                value={formData.superAdminEmail}
                onChange={(e) => handleChange('superAdminEmail', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.superAdminEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="admin@techvoice.com"
              />
              {errors.superAdminEmail && (
                <p className="text-xs text-red-500 mt-1">{errors.superAdminEmail}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.superAdminPassword}
                  onChange={(e) => handleChange('superAdminPassword', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    errors.superAdminPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Min. 6 characters"
                />
                {errors.superAdminPassword && (
                  <p className="text-xs text-red-500 mt-1">{errors.superAdminPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.superAdminPhone}
                  onChange={(e) => handleChange('superAdminPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="+1234567890"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Create Service Provider
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceProviderOnboardingModal;
